import { useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';
import {
  getScheduleConstraints,
  refineScheduleConfiguration,
  ScheduleConstraintCode,
} from 'services/api';
import {
  isConstraintChecked,
  setAllConstraintsMode,
  areAllConstraintsChecked,
  getCheckedConstraintsCount,
} from 'features/schedule-configuration/model';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import { scheduleSelectors } from 'features/schedule/redux';
import type { DifficultyDistributionModelCode, ScheduleConstraint } from 'services/api';

export type UseRefineModalParams = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  configurationId: string | undefined;
  timetableId: string | undefined;
  initialConstraints: ScheduleConstraint[];
};

export const useRefineModal = ({
  open,
  onClose,
  onSuccess,
  configurationId,
  timetableId,
  initialConstraints,
}: UseRefineModalParams) => {
  const difficultyDistribution = useAppSelector(scheduleSelectors.getDifficultyDistribution);
  const [constraints, setConstraints] = useState<ScheduleConstraint[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [difficultyDistributionModelCode, setDifficultyDistributionModelCode] =
    useState<DifficultyDistributionModelCode | null>(null);
  const [allConstraints, setAllConstraints] = useState<ScheduleConstraint[]>([]);

  const allChecked = areAllConstraintsChecked(constraints, [...allConstraints]);

  const indeterminate = constraints.some((c) => c.mode === 'ENABLED') && !allChecked;
  const checkedCount = getCheckedConstraintsCount(constraints);

  const handleCheckAll = (e: { target: { checked: boolean } }) =>
    setConstraints((prev) =>
      setAllConstraintsMode(e.target.checked ? 'ENABLED' : 'DISABLED', prev),
    );

  const handleConstraintChange = (code: ScheduleConstraint['code'], checked: boolean) => {
    setConstraints((prev) =>
      prev.map((constraint) =>
        constraint.code === code
          ? { ...constraint, mode: checked ? 'ENABLED' : 'DISABLED' }
          : constraint,
      ),
    );
  };

  const handleDifficultyDistributionModelChange = (value: DifficultyDistributionModelCode) =>
    setDifficultyDistributionModelCode(value);

  const handleSubmit = useCallback(() => {
    if (!configurationId || !timetableId) {
      return;
    }

    setSubmitting(true);
    refineScheduleConfiguration({
      configurationId,
      timetableId,
      constraints: constraints,
      difficultyDistributionModelCode:
        constraints.find(
          (constraint) =>
            constraint.code === ScheduleConstraintCode.DAILY_DIFFICULTY_DISTRIBUTION_CONFLICT,
        )?.mode === 'ENABLED'
          ? difficultyDistributionModelCode
          : null,
    })
      .then(() => {
        notification.success({
          message: 'Успешно',
          description: 'Новое расписание генерируется',
        });
        onClose();
        onSuccess?.();
      })
      .catch(() => {
        setSubmitting(false);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [
    configurationId,
    timetableId,
    constraints,
    onClose,
    onSuccess,
    difficultyDistributionModelCode,
  ]);

  useEffect(() => {
    getScheduleConstraints('SANPIN').then((res) => {
      setAllConstraints(res);
    });
  }, []);

  useEffect(() => {
    if (open && allConstraints.length) {
      const merged = allConstraints.map((constraint) => ({
        ...constraint,
        mode:
          initialConstraints.find((initialConstraint) => initialConstraint.code === constraint.code)
            ?.mode ?? ('DISABLED' as const),
      }));
      setConstraints(merged);
      setDifficultyDistributionModelCode(
        difficultyDistribution?.difficultyDistributionModelCode ?? null,
      );
    }
  }, [open, allConstraints, initialConstraints, difficultyDistribution]);

  return {
    state: {
      submitting,
      allChecked,
      indeterminate,
      checkedCount,
      constraints,
      difficultyDistributionModelCode,
    },
    methods: {
      handleCheckAll,
      handleConstraintChange,
      handleDifficultyDistributionModelChange,
      handleSubmit,
      handleClose: onClose,
      isConstraintChecked: (code: ScheduleConstraint['code']) =>
        isConstraintChecked(constraints, code),
    },
  };
};
